// Done — success page ported pixel-true from prototype DonePage.
// Confetti, pedestal-of-glow with bobbing celebrating Astro, hero
// title, 4-stat grid (Accounts/Opps/Cases/Dashboards), and quick
// actions: Open in Salesforce, Copy demo link, Playbook, New run.
//
// PROD-A3 (2026-04-21): when the run lands as ``complete_with_errors``
// (job config carries ``partial_success=true`` because at least one
// analytics sub-step failed silently — see
// ``pipeline_runner.collect_analytics_failures``) we swap the
// celebration for an amber "Demo built with errors" hero. The data
// layer DID land so we keep the "Open in Salesforce" CTA; we just
// surface the analytics failure summary inline and route the SE back
// to the Monitor's expandable error panel for the full bucketed
// breakdown. Dropping the user on confetti when nothing analytical
// deployed is the exact UX bug that motivated this hardening pass.
//
// 2026-05-18: ``status='failed'`` and ``status='cancelled'`` jobs were
// also routing through Monitor → /done (see Monitor.tsx isNowTerminal
// gate at line ~115) but Done.tsx only branched on ``isPartial``, so
// hard-failed jobs rendered the celebration UI verbatim — including
// "Your demo is ready!" hero, confetti, and an "Open in Salesforce"
// button pointing at an empty/half-broken org. Verified on prod job
// 75c6d768 where Phase 2's ``enable_audit_fields_perm`` step crashed
// with ``Permset metadata missing — repo state inconsistent`` and the
// user saw confetti + "built in 14 min, 16 stories live". This is the
// single worst UX bug behind the "I thought it worked" reports.
// Adding a third hero variant: red ``Run failed`` for failed/cancelled
// with the platform error string surfaced inline + CTA back to Monitor
// for the steps log + traceback.

import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Confetti } from "@/components/common/Confetti";
import { DemoReadinessPanel } from "@/components/done/DemoReadinessPanel";
import { useConfigStore } from "@/lib/store/configStore";
import { useUiStore } from "@/lib/store/uiStore";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { VOLUMES } from "@/lib/data";

// Open-in-Salesforce always routes through /api/org/launch which 302's
// to frontdoor.jsp with a fresh access_token, so the user lands inside
// Lightning without being bounced to the login page. Fall back to the
// raw instance URL only when we have no active session.
function resolveOrgUrl(
  runOrg: string | undefined,
  instanceUrl: string | undefined,
  orgConnected: boolean,
  retPath: string = "/lightning/page/home",
): string {
  if (orgConnected) {
    return `/api/org/launch?path=${encodeURIComponent(retPath)}`;
  }
  if (instanceUrl) {
    try {
      const u = new URL(instanceUrl);
      const host = u.host.replace(".my.salesforce.com", ".lightning.force.com");
      return `${u.protocol}//${host}`;
    } catch {
      return instanceUrl;
    }
  }
  if (runOrg) {
    return `https://${runOrg}.lightning.force.com`;
  }
  return "https://login.salesforce.com";
}

export function Done(): JSX.Element {
  const { runId = "" } = useParams();
  const navigate = useNavigate();
  const { config, reset } = useConfigStore();
  const showToast = useUiStore((s) => s.showToast);
  const auth = useAuth();
  const queryClient = useQueryClient();

  // Landing on the Done page is the cleanest signal that a run finished.
  // Force the notification bell to refresh right away so the user sees
  // their success notification without waiting for the next poll tick.
  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: ["notifications"] });
  }, [queryClient, runId]);

  const { data: run } = useQuery({
    queryKey: ["run", runId],
    queryFn: () => api.getRun(runId),
  });

  // We deliberately do NOT surface the pack name on the success page.
  // The previous implementation hardcoded lookups against a 3-tile
  // seed list, which silently mis-labelled runs whenever the SE built
  // off a non-built-in pack — every unknown profileId fell back to
  // PACKS[0] = "Cirrus Renewable Energy" regardless of what was
  // actually deployed. Even with a live `api.listPacks()` resolution
  // the brand label adds noise: SEs run dozens of packs back-to-back
  // and the success screenshot they share with prospects reads cleaner
  // without an industry name baked in. The pack object is no longer
  // referenced by this page; the volume preset is the only stat-grid
  // / hero-subtitle source we keep.
  const vol =
    VOLUMES.find((v) => v.id === (run?.volume ?? "").toLowerCase()) ||
    VOLUMES.find((v) => v.id === config.volume) ||
    VOLUMES[2];

  const stats: [string, number | string][] = [
    ["Accounts", vol.accounts],
    ["Opportunities", vol.opps],
    ["Cases", vol.cases],
    ["Stories", run?.storiesCount ?? config.stories.length],
  ];

  // TDSX runs (output_mode=hyper) expose /api/jobs/{id}/download-hyper.
  // We can't tell at this layer what mode was picked, so always offer it
  // when the run was kicked off in tdsx_only mode or had the tdsx layer.
  const isTdsxRun =
    config.mode === "tdsx_only" ||
    (run?.layers?.includes("tdsx") ?? false) ||
    config.pipeline.tdsx;

  const isPartial = run?.status === "complete_with_errors";
  const isFailed = run?.status === "failed" || run?.status === "cancelled";
  const failureSummary = run?.analyticsFailureSummary ?? [];
  // Heroku worker workers truncate at 300 chars when writing
  // ``j.error_message``; show the user the full string via the Monitor
  // CTA. ``cancelled`` jobs use the same message slot for "Job cancelled
  // by user" — render distinct copy for them so we don't suggest the
  // user click "View error log" for an explicit cancellation.
  const errorMsg = (run?.errorMsg ?? "").trim();
  const isCancelled = run?.status === "cancelled";

  return (
    <div
      style={{
        maxWidth: 760,
        margin: "60px auto",
        padding: "0 24px",
        textAlign: "center",
        position: "relative",
      }}
    >
      {!isPartial && !isFailed && <Confetti />}

      {/* Pedestal of glow / warning halo */}
      <div
        style={{
          position: "relative",
          height: 180,
          marginBottom: 8,
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -40%)",
            width: 260,
            height: 260,
            borderRadius: "50%",
            background: isFailed
              ? "radial-gradient(circle, rgba(217,48,37,.32) 0%, rgba(217,48,37,.12) 35%, transparent 65%)"
              : isPartial
                ? "radial-gradient(circle, rgba(243,154,55,.32) 0%, rgba(243,154,55,.14) 35%, transparent 65%)"
                : "radial-gradient(circle, rgba(245,184,0,.28) 0%, rgba(29,112,201,.16) 35%, transparent 65%)",
            filter: "blur(4px)",
            animation: "donePulse 2.2s ease-in-out infinite",
          }}
        />
        {isFailed ? (
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: 130,
              height: 130,
              borderRadius: "50%",
              background: "white",
              border: "4px solid var(--red, #d93025)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 64,
              lineHeight: 1,
              color: "var(--red, #d93025)",
              fontWeight: 700,
              filter: "drop-shadow(0 18px 24px rgba(3,45,96,.18))",
            }}
          >
            ✕
          </div>
        ) : isPartial ? (
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: 130,
              height: 130,
              borderRadius: "50%",
              background: "white",
              border: "4px solid var(--orange)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 64,
              lineHeight: 1,
              color: "var(--orange)",
              fontWeight: 700,
              filter: "drop-shadow(0 18px 24px rgba(3,45,96,.18))",
            }}
          >
            !
          </div>
        ) : (
          <img
            src="/assets/astro-celebrating.png"
            alt=""
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              height: 170,
              filter: "drop-shadow(0 18px 24px rgba(3,45,96,.22))",
              animation: "doneBob 2.8s ease-in-out infinite",
            }}
          />
        )}
      </div>
      <style>{`
        @keyframes donePulse {
          0%, 100% { opacity: .85; transform: translate(-50%,-40%) scale(1); }
          50%      { opacity: 1;   transform: translate(-50%,-40%) scale(1.08); }
        }
        @keyframes doneBob {
          0%, 100% { transform: translate(-50%,-50%) rotate(-1.5deg); }
          50%      { transform: translate(-50%,-55%) rotate(2deg); }
        }
      `}</style>

      <h1
        style={{
          fontSize: 34,
          color: isFailed
            ? "var(--red, #d93025)"
            : isPartial
              ? "var(--orange)"
              : "var(--eb-15)",
          margin: "4px 0 10px",
          letterSpacing: "-0.01em",
          fontWeight: 700,
        }}
      >
        {isCancelled
          ? "Run cancelled"
          : isFailed
            ? "Run failed"
            : isPartial
              ? "Demo built with deployment errors"
              : "Your demo is ready!"}
      </h1>
      <p
        style={{
          fontSize: 14,
          color: "var(--ink-2)",
          maxWidth: 520,
          margin: "0 auto 16px",
        }}
      >
        {isCancelled ? (
          <>
            This run was cancelled before it could finish.
            Nothing was deployed to the org.
          </>
        ) : isFailed ? (
          <>
            The pipeline failed before it could finish provisioning.
            The org may be in a partial state — open the run monitor
            for the full step log and platform error.
          </>
        ) : isPartial ? (
          <>
            {vol.label} demo — data landed in{" "}
            {run?.duration ?? `${vol.duration} min`}, but one or more
            analytics layers didn't deploy. Open the run monitor for the
            full breakdown.
          </>
        ) : (
          <>
            {vol.label} demo — built in {run?.duration ?? `${vol.duration} min`},{" "}
            {run?.storiesCount ?? config.stories.length} stories live.
          </>
        )}
      </p>

      {isFailed && errorMsg && (
        <div
          role="alert"
          style={{
            margin: "0 auto 26px",
            maxWidth: 520,
            padding: "12px 16px",
            borderRadius: 10,
            border: "1px solid var(--red, #d93025)",
            background: "rgba(217,48,37,.08)",
            color: "var(--ink)",
            textAlign: "left",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: 12,
              textTransform: "uppercase",
              letterSpacing: ".06em",
              color: "var(--red, #d93025)",
              marginBottom: 6,
            }}
          >
            {isCancelled ? "Cancellation reason" : "Error"}
          </div>
          <div style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}>
            {errorMsg}
          </div>
        </div>
      )}

      {isPartial && failureSummary.length > 0 && (
        <div
          role="alert"
          style={{
            margin: "0 auto 26px",
            maxWidth: 520,
            padding: "12px 16px",
            borderRadius: 10,
            border: "1px solid var(--orange)",
            background: "rgba(243,154,55,.08)",
            color: "var(--ink)",
            textAlign: "left",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: 12,
              textTransform: "uppercase",
              letterSpacing: ".06em",
              color: "var(--orange)",
              marginBottom: 6,
            }}
          >
            What didn't deploy
          </div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {failureSummary.slice(0, 3).map((line) => (
              <li key={line} style={{ marginBottom: 4 }}>
                {line}
              </li>
            ))}
            {failureSummary.length > 3 && (
              <li style={{ color: "var(--ink-3)" }}>
                +{failureSummary.length - 3} more — see the run monitor.
              </li>
            )}
          </ul>
        </div>
      )}
      {!isPartial && !isFailed && <div style={{ height: 16 }} />}

      {/* Stat grid hidden on failed/cancelled runs — the volume preset
          numbers reflect the *target*, not what actually landed. Showing
          "200 / 3,000 / 20,000" on a Phase-2-failed run is the exact UX
          lie that motivated this hardening (job 75c6d768, 2026-05-18). */}
      {!isFailed && (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 12,
          marginBottom: 28,
          textAlign: "left",
        }}
      >
        {stats.map(([k, v]) => (
          <div key={k} className="card" style={{ padding: 16 }}>
            <div
              style={{
                fontSize: 10,
                color: "var(--ink-3)",
                textTransform: "uppercase",
                letterSpacing: ".08em",
              }}
            >
              {k}
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: "var(--ink)",
                marginTop: 2,
              }}
            >
              {typeof v === "number" ? v.toLocaleString() : v}
            </div>
          </div>
        ))}
      </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 10,
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        {(isPartial || isFailed) && (
          <button
            className="btn primary lg"
            onClick={() => navigate(`/monitor/${runId}`)}
          >
            {isFailed ? "View error log ↗" : "View deployment errors ↗"}
          </button>
        )}
        {/* Open-in-Salesforce / Download-tdsx hidden on failed runs —
            the org is in a partial state and the CTA is misleading. */}
        {!isFailed && (
          isTdsxRun ? (
            <a
              className={isPartial ? "btn ghost lg" : "btn primary lg"}
              href={`/api/jobs/${runId}/download-hyper`}
              target="_blank"
              rel="noreferrer"
            >
              ↓ Download .tdsx
            </a>
          ) : (
            <a
              className={isPartial ? "btn ghost lg" : "btn primary lg"}
              href={resolveOrgUrl(run?.org, auth.org?.instance_url, auth.orgConnected)}
              target="_blank"
              rel="noreferrer"
            >
              Open in Salesforce ↗
            </a>
          )
        )}
        {!isFailed && (
          <button
            className="btn ghost"
            onClick={() => {
              const link = `${window.location.origin}/runs/${runId}`;
              navigator.clipboard?.writeText(link);
              showToast("Demo link copied");
            }}
          >
            Copy demo link
          </button>
        )}
        {!isPartial && !isFailed && (
          <button
            className="btn ghost"
            onClick={() => navigate("/playbooks")}
          >
            Playbook
          </button>
        )}
        <button
          className="btn ghost"
          onClick={() => {
            reset();
            navigate("/");
          }}
        >
          {isFailed ? "Try again" : "New run"}
        </button>
      </div>

      {/*
        PR 5 — Demo readiness panel. Hidden on failed runs (the
        org is in a partial state and the polisher's check matrix
        isn't useful) and on TDSX-only runs (no live org to poll).
      */}
      {!isFailed && !isTdsxRun && <DemoReadinessPanel />}
    </div>
  );
}
